import React, { PropsWithChildren, createContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { DomainEnum, getVendorPermissions } from '@/api';
import { getUserId } from '@/helpers/session';

import { ALL_PAGE_DOMAINS } from './capabilities';

export type PermissionsValue = {
  /** Domains the user holds (across any vendor). */
  domains: Set<DomainEnum>;
  /** Vendor ids the user is permitted on, keyed by domain. */
  vendorIdsByDomain: Map<DomainEnum, Set<string>>;
  loading: boolean;
};

export const PermissionsContext = createContext<PermissionsValue | null>(null);

/**
 * Fetches the user's vendor permissions once and exposes them via context.
 * Mounted inside PrivateRoute so it covers the whole authenticated app and is
 * torn down on logout. Replaces the per-page getVendorPermissions queries.
 */
export const PermissionsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const userId = getUserId() || '';

  const { data, loading } = useQuery(getVendorPermissions, {
    variables: { userId, entityType: 'Vendor', domains: ALL_PAGE_DOMAINS },
    skip: !userId,
    fetchPolicy: 'cache-first',
  });

  const value = useMemo<PermissionsValue>(() => {
    const permissions = data?.getUser?.user?.permissions ?? [];
    const domains = new Set<DomainEnum>();
    const vendorIdsByDomain = new Map<DomainEnum, Set<string>>();

    permissions.forEach((permission) => {
      domains.add(permission.domain);
      const vendorIds = vendorIdsByDomain.get(permission.domain) ?? new Set<string>();
      vendorIds.add(permission.entityId);
      vendorIdsByDomain.set(permission.domain, vendorIds);
    });

    return { domains, vendorIdsByDomain, loading };
  }, [data, loading]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};
